import React, { useState, useEffect } from 'react';
import Link from '@mui/material/Link';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Card,
  CardHeader,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { IconButton, Collapse } from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AddBoxIcon from '@mui/icons-material/AddBox';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';    
 

import Button from '@mui/material/Button';
import Title from '../../components/Title';
import SearchBar from '../../components/SearchBar';
import EditModal from '../../components/EditModal';
import AddModal from '../../components/AddModal';
import LoadStatsModal from '../../components/LoadStatsModal';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, NA_LABEL } from '../../common/AppConstants';
import { getRelatedDataMap, notifyClientErrors } from '../../common/AppUtils';

const TITLE = 'Subject Class';
const GROUPED_DATA_API = 'time-table/template/subject-class-grouped/';
const SUBJECT_SESSION_API = 'time-table/template/subject-class/';
const NEW_ELECTIVE_API = 'time-table/template/elective-new/';
const ELECTIVE_SESSION_API = 'time-table/template/elective-class/';
const ELECTIVE_SESSION_BULK_API = 'time-table/template/elective-class-bulk/'; // Works with the session_list of elective_group for a elective_subject_template
const COPY_CLASSES_API = 'time-table/template/clone-class/';
const NO_DATA_AVAILABLE_STRING =
  'No data available, please add Sections !!!';

const column_map = {
  subject: {
    attr: 'subject',
    editable: true,
    extra: false,
    label: 'Subject',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Subject',
  },
  slot_size: {
    attr: 'slot_size',
    editable: true,
    extra: false,
    label: 'Slot Size',
    related: false,
    required: true,
    type: 'text',
    help_text: 'Slot Size',
  },
  staff: {
    attr: 'staff',
    editable: true,
    extra: false,
    label: 'Teacher',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Teacher',
  },
  classroom: {
    attr: 'classroom',
    editable: true,
    extra: false,
    label: 'Classroom',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Classroom',
  }
}

const subjectAddColumns = [
  column_map.subject,
  column_map.slot_size,
  column_map.staff,
  column_map.classroom,
];

const subjectEditColumns = [
  column_map.slot_size,
  column_map.staff,
  column_map.classroom,
];

const newElectiveColumns = [
  {
    attr: 'name',
    editable: true,
    extra: false,
    label: 'Name',
    related: false,
    required: true,
    type: 'text',
    help_text: 'Elective subject',
  },
];

const electiveAddColumns = [
  column_map.subject,
  column_map.slot_size,
  column_map.staff,
  column_map.classroom,
];

const electiveEditColumns = [
  column_map.subject,
  column_map.slot_size,
  column_map.staff,
  column_map.classroom,
];

const electiveOptionAddColumns = [
  column_map.subject,
  // column_map.slot_size,
  column_map.staff,
  column_map.classroom,
];

const copyClassesColumns = [
  {
    attr: 'section',
    editable: true,
    extra: false,
    label: 'Import from',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Import classes from Section',
  },
];

const EXPAND_BG_COLOR = '#FDFDFD';  // '#FDFDFD'

const SUBJECT_HEADER_COLOR = "#FFFFFF"  // F8F8F8

const GRADIENT_SHARP = "sharp";
const getColorGradient = (color_list, type=GRADIENT_SHARP) => {
  if (type===GRADIENT_SHARP) {
    return `linear-gradient(90deg,
      ${color_list.map((c, i, arr) => {
        const start = (i / arr.length) * 100;
        const end = ((i + 1) / arr.length) * 100;
        return `${c} ${start}%, ${c} ${end}%`;
      }).join(", ")}
    )`;
  }
  return `linear-gradient(90deg, ${color_list.join(", ")})`;
}

const getSubjectColorList = (subject_dict, relatedMap) => {
  const color = relatedMap.subject?.[subject_dict.id]?.color || "#f5f5f5";
  return [color];
}

const getElectiveColorList = (elective_group_template_list, relatedMap) => {
  let color_list =
    elective_group_template_list.length === 0
    ? []
    : (elective_group_template_list[0]?.session_list || [])
      .map(session => {
        const subjectId = session?.subject;
        return relatedMap.subject?.[subjectId]?.color;
      })
      .filter(Boolean);
  if (color_list.length === 0) {
    color_list = ["#f5f5f5"];
  }
  return color_list;
}

const getTotalSectionClasses = (section_dict) => {
  const total_subject_session = section_dict['subject_list'].reduce((total, subject_dict) => 
      total + (subject_dict.session_list || []).reduce((sum, session_dict) => 
        sum + (session_dict.slot_size || 0), 0
      ), 0
    );
    const total_elective_session = section_dict.elective_subject_template_list.reduce((total, elective_dict) =>
      total + (elective_dict.elective_group_template_list || []).reduce((elective_total, elective_group) =>
        elective_total + Math.max(
          0,
          ...(elective_group.session_list || []).map(
            (session) => session.slot_size || 0
          )
        ), 0
      ), 0
    );
    return total_subject_session + total_elective_session;
}

export default function SubjectClass() {
  const { addNotification } = useNotification();
  const [isLoading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [relatedData, setRelatedData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedMap, setExpandedMap] = useState({});
  const [isLoadStatsModalOpen, setIsLoadStatsModalOpen] = useState(false);

  const relatedMap = React.useMemo(
    () => getRelatedDataMap(relatedData),
    [relatedData]
  );

  // Filtered data based on search
  const filteredRows = rows.filter(row_item =>
    relatedMap.section?.[row_item.id]?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const fetchData = async () => {
      refreshData();
    };
    fetchData();
  }, []);

  const refreshData = async (event) => {
    setLoading(true);

    await httpRequest({
      method: 'GET',
      url: GROUPED_DATA_API,
      onSuccess: (data) => {
        setRows(data.rows);
        setRelatedData(data.relatedData);
        if (data.rows.length > 0) {
          // Expand First Section Card
          // toggleExpand(data.rows[0].id); // Doesn't work due to re-renders maybe
          setExpandedMap({
            [data.rows[0].id]: true
          });
        }
      },
      onFailure: (data) => {
        setRows([]);
        setRelatedData({});
      },
      addNotification,
    });

    setLoading(false);
  };

  const toggleExpand = (id) => {
    setExpandedMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleLoadStatsClick = () => {
    setIsLoadStatsModalOpen(true);
  };

  const handleLoadStatsModalClose = () => {
    setIsLoadStatsModalOpen(false);
  };

  const SectionCard = ({ section_row, section_dict }) => {
    const [isSubjectAddOpen, setSubjectAddOpen] = useState(false);
    const [subjectEdit, setSubjectEdit] = useState(null);
    const [isSubjectEditOpen, setSubjectEditOpen] = useState(false);
    
    const [isNewElectiveAddOpen, setNewElectiveAddOpen] = useState(false);
    const [electiveAdd, setElectiveAdd] = useState(null);
    const [electiveEdit, setElectiveEdit] = useState(null);
    const [isElectiveEditOpen, setElectiveEditOpen] = useState(false);
    const [electiveOptionAdd, setElectiveOptionAdd] = useState(null);

    const [isCopyClassesOpen, setCopyClassesOpen] = useState(false);

    const total_session = getTotalSectionClasses(section_dict);
    const incomplete_data = total_session === 0;

    const section_name = relatedMap.section?.[section_dict.id]?.name;

    //////////////////////
    /////// Subject //////
    //////////////////////

    // Add Subject Session
    const handleSubjectAddClose = () => {
      setSubjectAddOpen(false);
    };

    const handleSubjectAddSave = async (addFormData, onAddSuccess, onAddFailure) => {
      const shift = relatedMap.section[section_dict.id].shift;
      const finalFormData = {
        ...addFormData,
        shift: shift,
        section: section_dict.id,
      };
      
      await httpRequest({
        method: 'POST',
        url: SUBJECT_SESSION_API,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) => {
              if (row.id !== section_dict.id) return row;

              const subjectId = data.subject;
              let subjectExists = false;
              const updatedSubjects = row.subject_list.map((subject) => {
                if (subject.id === subjectId) {
                  subjectExists = true;
                  return {
                    ...subject,
                    session_list: [
                      ...subject.session_list,
                      {
                        id: data.id,
                        slot_size: data.slot_size,
                        staff: data.staff,
                        classroom: data.classroom,
                      },
                    ],
                  };
                }
                return subject;
              });

              // Case 2: new subject
              if (!subjectExists) {
                updatedSubjects.push({
                  id: subjectId,
                  session_list: [
                    {
                      id: data.id,
                      slot_size: data.slot_size,
                      staff: data.staff,
                      classroom: data.classroom,
                    },
                  ],
                });
              }
              return {
                ...row,
                subject_list: updatedSubjects,
              };
            }),
          );
          onAddSuccess();
        },
        onFailure: (data) => {
          onAddFailure(data);
        },
        addNotification,
      });
    };

    const duplicateSubjectSession = async (subject_dict) => {
      // console.log("Current group data", rows);
      const refSess = subject_dict.session_list.at(-1);
      const formData = {
        subject: subject_dict.id,
        slot_size: refSess.slot_size,
        staff: refSess.staff,
        classroom: refSess.classroom,
      };

      await handleSubjectAddSave(
        formData,
        () => addNotification("Successful!!!"),
        (error) => notifyClientErrors(error)
      );
    };

    // Edit Subject Session
    const handleSubjectEditClick = (subject_id, session_dict) => {
      setSubjectEdit({
        ...session_dict,
        subject: subject_id,
      });
    };

    useEffect(() => {
      if (subjectEdit !== null && subjectEdit !== undefined) {
        // Only open the modal after the row has been selected
        setSubjectEditOpen(true);
      }
    }, [subjectEdit]);

    const handleSubjectEditClose = () => {
      setSubjectEditOpen(false);
      setSubjectEdit(null);
    };

    const handleSubjectEditSave = async (updatedEditRow, onEditFailure) => {
      const shift = relatedMap.section[section_dict.id].shift;
      const finalFormData = {
        ...updatedEditRow,
        shift: shift,
        section: section_dict.id,
      };
      
      await httpRequest({
        method: 'PUT',
        url: SUBJECT_SESSION_API,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) => {
              if (row.id !== section_dict.id) return row;

              const updatedSubjects = row.subject_list.map((subject) => {
                if (subject.id !== data.subject) return subject;

                const updatedSessions = subject.session_list.map((session) => {
                  if (session.id !== data.id) return session;

                  return {
                    ...session,
                    ...data, // overwrite updated fields
                  };
                });

                return {
                  ...subject,
                  session_list: updatedSessions,
                };
              });

              return {
                ...row,
                subject_list: updatedSubjects,
              };
            })
          );
          handleSubjectEditClose();
        },
        onFailure: (data) => {
          onEditFailure(data);
        },
        addNotification,
      });
    };

    // Delete Subject Session
    const handleSubjectSessionDelete = async (session_dict) => {
      await httpRequest({
        method: 'DELETE',
        url: SUBJECT_SESSION_API,
        data: session_dict,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) => {
              if (row.id !== section_dict.id) return row;

              const updatedSubjects = row.subject_list
                .map((subject) => {
                  if (subject.id !== session_dict.subject) return subject;

                  const updatedSessions = subject.session_list.filter(
                    (session) => session.id !== data.id
                  );

                  // If no sessions left -> mark for removal
                  if (updatedSessions.length === 0) {
                    return null;
                  }

                  return {
                    ...subject,
                    session_list: updatedSessions,
                  };
                })
                .filter(Boolean); // removes null subjects

              return {
                ...row,
                subject_list: updatedSubjects,
              };
            })
          );
        },
        onFailure: (data) => {
          notifyClientErrors(data);
        },
        addNotification,
      });
    };

    const SubjectCard = ({ section_row, subject_dict }) => {
      const subject_name = relatedMap.subject?.[subject_dict.id]?.name;
      const session_list = subject_dict.session_list || [];
      const color_gradient = getColorGradient(getSubjectColorList(subject_dict, relatedMap), GRADIENT_SHARP);

      return (
        <Card
          // variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: 3
            },
            position: 'relative',
            width: "100%",
            gap: 0.25,
            pb: 1,
            borderRadius: 2,
          }}
        >
          {/* Season header */}
          <CardHeader
            avatar={
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  background: color_gradient,
                  borderRadius: '4px',  // '4px' -> Square, '50%' -> Round
                  display: 'inline-block',
                  border: '1px solid rgba(0,0,0,0.12)',
                }}
              />
            }
            title={
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontSize: "0.7rem",
                }}
              >
                {subject_name}
              </Typography>
            }
            action={
              <Button
                variant='outlined'
                endIcon={<AddBoxIcon sx={{ fontSize: "0.9rem" }} />}
                onClick={() => duplicateSubjectSession(subject_dict)}
                sx={{
                  minHeight: "unset",
                  height: "auto",
                  padding: "0px 6px",
                  marginX: "8px",
                  fontSize: "0.7rem",
                  lineHeight: 1.2,
                  textTransform: "none",
                  "&:hover": { color: "primary.main" },
                  "& .MuiButton-endIcon": {
                    marginLeft: "4px",
                  },
                }}
              >
                Add session
              </Button>
            }
            sx={{
              px: 1,
              py: 0,
              alignItems: 'center',
              '& .MuiCardHeader-action': {
                alignSelf: 'center',
              },
            }}
            slotProps={{
              content: { sx: { py: 0.5 } },
            }}
          />
          <Divider sx={{ mb: 0.5 }}/>

          {/* Session list */}
          <Box sx = {{ px: 1, width: "100%", }}>
            <Stack spacing={ 0.25 } sx={{ width: "100%" }}>
              {session_list.map((session_dict, sess_idx) => (
                <Box
                  key={session_dict.id}
                  sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    gap: 1,
                    borderRadius: 1,
                    // bgcolor: i % 2 ? BG_COLOR_EVEN : BG_COLOR_ODD,
                    "&:hover": { bgcolor: "action.hover" },
                    "&:hover .session-edit-btn": { opacity: 1 },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      flexShrink: 0,
                      bgcolor: "grey.200",
                      color: "text.secondary",
                      borderRadius: 1,
                      px: 0.7,
                      fontWeight: 700,
                      fontSize: "0.6rem",
                      textTransform: "uppercase",
                      cursor: "default",
                      lineHeight: 1.8,
                    }}
                  >
                    {session_dict.slot_size}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ flex: 1, fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {relatedMap.staff?.[session_dict.staff]?.pdf_label || NA_LABEL}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ flex: 1, fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {relatedMap.classroom?.[session_dict.classroom]?.name || NA_LABEL}
                  </Typography>
                  <IconButton
                    className="session-edit-btn"
                    size="small"
                    onClick={() => handleSubjectEditClick(subject_dict.id, session_dict)}
                    sx={{
                      flexShrink: 0,
                      opacity: 0.1, // 0
                      transition: "opacity 0.15s",
                      p: 0.3,
                      color: "primary.main",
                      // ml: "auto",
                    }}
                  >
                    <EditTwoToneIcon sx={{ fontSize: "0.85rem" }} />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Box>
        </Card>
      );
    };

    //////////////////////
    /////// Elective /////
    //////////////////////

    const handleNewElectiveClose = () => {
      setNewElectiveAddOpen(false);
    };

    const handleNewElectiveSave = async (addFormData, onAddSuccess, onAddFailure) => {
      const finalFormData = {
        ...addFormData,
        section: section_dict.id,
      };
      
      await httpRequest({
        method: 'POST',
        url: NEW_ELECTIVE_API,
        data: finalFormData,
        onSuccess: (data) => {
          // Related Data since not doing full fetch here
          setRelatedData((prev) => ({
            ...prev,
            elective_subject_template: [...(prev.elective_subject_template || []), data],
          }));

          setRows((prevRows) =>
            prevRows.map((row) => {
              if (row.id !== section_dict.id) return row;

              return {
                ...row,
                elective_subject_template_list: [
                  ...row.elective_subject_template_list,
                  {
                    id: data.id,
                    elective_group_template_list: [],
                  },
                ],
              };
            })
          );
          onAddSuccess();
        },
        onFailure: (data) => {
          onAddFailure(data);
        },
        addNotification,
      });
    };

    // Add Elective Session
    const handleElectiveAddClose = () => {
      setElectiveAdd(null);
    };

    const createElectiveSession = async (finalFormData, onAddSuccess, onAddFailure) => {
      await httpRequest({
        method: 'POST',
        url: ELECTIVE_SESSION_API,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) => {
              if (row.id !== section_dict.id) return row;

              return {
                ...row,
                elective_subject_template_list: (row.elective_subject_template_list || []).map((elective) => {
                  if (elective.id !== data.elective_subject_template) return elective;

                  let groupExists = false;
                  const updatedGroups = (elective.elective_group_template_list || []).map((group) => {
                    if (group.id !== data.elective_group_template) return group;

                    groupExists = true;
                    return {
                      ...group,
                      session_list: [
                        ...(group.session_list || []),
                        {
                          id: data.id,
                          subject: data.subject,
                          slot_size: data.slot_size,
                          staff: data.staff,
                          classroom: data.classroom,
                        },
                      ],
                    };
                  });

                  // If group does NOT exist → create it
                  if (!groupExists) {
                    updatedGroups.push({
                      id: data.elective_group_template,
                      session_list: [
                        {
                          id: data.id,
                          subject: data.subject,
                          slot_size: data.slot_size,
                          staff: data.staff,
                          classroom: data.classroom,
                        },
                      ],
                    });
                  }

                  return {
                    ...elective,
                    elective_group_template_list: updatedGroups,
                  };
                }),
              };
            })
          );
          onAddSuccess();
        },
        onFailure: (data) => {
          onAddFailure(data);
        },
        addNotification,
      });
    }

    const handleElectiveAddSave = async (addFormData, onAddSuccess, onAddFailure) => {
      const shift = relatedMap.section[section_dict.id].shift;
      const finalFormData = {
        ...addFormData,
        shift: shift,
        section: section_dict.id,
        elective_subject_template: electiveAdd.id,
      };
      
      await createElectiveSession(finalFormData, onAddSuccess, onAddFailure);
    };

    const handleDuplicateElective = async (elective_dict) => {
      // console.log("Current group data", rows);
      // No elective subject options/session present
      if (elective_dict.elective_group_template_list.length === 0) {
        setElectiveAdd(elective_dict);
        return;
      }

      const refSubjectGroup = elective_dict.elective_group_template_list.at(-1);
      const formData = {
        section: section_dict.id,
        elective_subject_template: elective_dict.id,
        elective_group_template: refSubjectGroup.id,
      };

      await httpRequest({
        method: 'POST',
        url: ELECTIVE_SESSION_BULK_API,
        data: formData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) => {
              if (row.id !== section_dict.id) return row;

              return {
                ...row,
                elective_subject_template_list: (row.elective_subject_template_list || []).map((elective) => {
                  if (elective.id !== elective_dict.id) return elective;

                  return {
                    ...elective,
                    elective_group_template_list: [
                      ...(elective.elective_group_template_list || []),
                      data, // direct insert
                    ],
                  };
                }),
              };
            })
          );
          addNotification("Successful!!!");
        },
        addNotification,
      });
    };

    // Edit Elective Session
    const handleElectiveEditClick = (elective_subject_template_id, subject_group_id, session_dict) => {
      setElectiveEdit({
        ...session_dict,
        elective_group_template: subject_group_id,
        elective_subject_template: elective_subject_template_id,
      });
    };

    useEffect(() => {
      if (electiveEdit !== null && electiveEdit !== undefined) {
        // Only open the modal after the row has been selected
        setElectiveEditOpen(true);
      }
    }, [electiveEdit]);

    const handleElectiveEditClose = () => {
      setElectiveEditOpen(false);
      setElectiveEdit(null);
    };

    const handleElectiveEditSave = async (updatedEditRow, onEditFailure) => {
      const shift = relatedMap.section[section_dict.id].shift;
      const finalFormData = {
        ...updatedEditRow,
        shift: shift,
        section: section_dict.id,
      };
      
      await httpRequest({
        method: 'PUT',
        url: ELECTIVE_SESSION_API,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) => {
              if (row.id !== section_dict.id) return row;

              return {
                ...row,
                elective_subject_template_list: (row.elective_subject_template_list || []).map((elective) => {
                  if (elective.id !== data.elective_subject_template) return elective;

                  return {
                    ...elective,
                    elective_group_template_list: (elective.elective_group_template_list || []).map((group) => {
                      if (group.id !== data.elective_group_template) return group;

                      return {
                        ...group,
                        session_list: (group.session_list || []).map((session) => {
                          const isTarget = session.id === data.id;

                          return {
                            ...session,
                            slot_size: data.slot_size,
                            ...(isTarget ? data : {}),
                          };
                        }),
                      };
                    }),
                  };
                }),
              };
            })
          );
          handleElectiveEditClose();
        },
        onFailure: (data) => {
          onEditFailure(data);
        },
        addNotification,
      });
    };

    // Delete Elective Session
    const handleElectiveSessionDelete = async (session_dict) => {
      await httpRequest({
        method: 'DELETE',
        url: ELECTIVE_SESSION_API,
        data: session_dict,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) => {
              if (row.id !== section_dict.id) return row;

              return {
                ...row,
                elective_subject_template_list: (row.elective_subject_template_list || []).map((elective) => {
                  if (elective.id !== session_dict.elective_subject_template) return elective;

                  const updatedGroups = (elective.elective_group_template_list || [])
                    .map((group) => {
                      if (group.id !== session_dict.elective_group_template) return group;

                      const updatedSessions = (group.session_list || []).filter(
                        (session) => session.id !== data.id
                      );

                      return {
                        ...group,
                        session_list: updatedSessions,
                      };
                    })
                    // Rremove empty subject_groups ONLY
                    .filter((group) => group.session_list.length > 0);

                  return {
                    ...elective,
                    elective_group_template_list: updatedGroups, // can be []
                  };
                }),
              };
            })
          );
        },
        onFailure: (data) => {
          notifyClientErrors(data);
        },
        addNotification,
      });
    };

    const handleElectiveOptionClose = () => {
      setElectiveOptionAdd(null);
    };

    const handleElectiveOptionSave = async (addFormData, onAddSuccess, onAddFailure) => {
      const shift = relatedMap.section[section_dict.id].shift;
      const refSess = electiveOptionAdd.subject_group_dict.session_list.at(0);
      const finalFormData = {
        ...addFormData,
        shift: shift,
        section: section_dict.id,
        elective_subject_template: electiveOptionAdd.elective_subject_template,
        elective_group_template: electiveOptionAdd.subject_group_dict.id,
        slot_size: refSess.slot_size,
      };

      await createElectiveSession(finalFormData, onAddSuccess, onAddFailure);
    };

    const ElectiveCard = ({ section_row, elective_dict }) => {
      const elective_name = relatedMap.elective_subject_template?.[elective_dict.id]?.name;
      const elective_group_template_list = elective_dict.elective_group_template_list || [];
      const color_gradient = getColorGradient(getElectiveColorList(elective_group_template_list, relatedMap), GRADIENT_SHARP);

      return (
        <Card
          // variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: 3
            },
            position: 'relative',
            width: "100%",
            gap: 0.25,
            pb: 1.5,
            borderRadius: 2,
          }}
        >
          {/* Season header */}
          <CardHeader
            avatar={
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  background: color_gradient,
                  borderRadius: '4px',  // '4px' -> Square, '50%' -> Round
                  display: 'inline-block',
                  border: '1px solid',
                  borderColor: '#AAAAAA', // Since multi colors, the border looks weird, hence adding grey mix
                }}
              />
            }
            title={
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontSize: "0.7rem",
                }}
              >
                {elective_name}
              </Typography>
            }
            action={
              <Button
                variant='outlined'
                endIcon={<AddBoxIcon sx={{ fontSize: "0.9rem" }} />}
                onClick={() => handleDuplicateElective(elective_dict)}
                sx={{
                  minHeight: "unset",
                  height: "auto",
                  padding: "0px 6px",
                  marginX: "8px",
                  fontSize: "0.7rem",
                  lineHeight: 1.2,
                  textTransform: "none",
                  "&:hover": { color: "primary.main" },
                  "& .MuiButton-endIcon": {
                    marginLeft: "4px",
                  },
                }}
              >
                Add session
              </Button>
            }
            sx={{
              px: 1,
              py: 0,
              alignItems: 'center',
              '& .MuiCardHeader-action': {
                alignSelf: 'center',
              },
            }}
            slotProps={{
              content: { sx: { py: 0.5 } },
            }}
          />
          <Divider sx={{ mb: 0.5 }}/>

          {/* Session list */}
          <Box sx = {{ px: 1, width: "100%", }}>
            <Stack spacing={ 1 } sx={{ width: "100%" }}>
              { elective_group_template_list.length === 0 ? (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500 }}
                >
                  Please add session for this elective
                </Typography>
              ) : (
                elective_group_template_list.map((subject_group_dict, grp_idx) => {
                  const session_list = subject_group_dict.session_list || [];
                  return (
                    <Box
                      key={subject_group_dict.id}
                      sx={{
                        display: "flex",
                        width: "100%",
                        alignItems: "center",
                        gap: 1,
                        borderRadius: 1,
                        // bgcolor: i % 2 ? BG_COLOR_EVEN : BG_COLOR_ODD,
                      }}
                    >
                      <Stack spacing={ 0.25 } sx={{ width: "100%" }}>
                        {session_list.map((session_dict, sess_idx) => (
                          <Box
                            key={session_dict.id}
                            sx={{
                              display: "flex",
                              width: "100%",
                              alignItems: "center",
                              gap: 1,
                              borderRadius: 1,
                              // bgcolor: i % 2 ? BG_COLOR_EVEN : BG_COLOR_ODD,
                              "&:hover": { bgcolor: "action.hover" },
                              "&:hover .session-edit-btn": { opacity: 1 },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                flexShrink: 0,
                                bgcolor: "grey.200",
                                color: "text.secondary",
                                borderRadius: 1,
                                px: 0.7,
                                fontWeight: 700,
                                fontSize: "0.6rem",
                                textTransform: "uppercase",
                                cursor: "default",
                                lineHeight: 1.8,
                              }}
                            >
                              {session_dict.slot_size}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ flex: 1, fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                              {relatedMap.subject?.[session_dict.subject]?.name || NA_LABEL}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ flex: 1, fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                              {relatedMap.staff?.[session_dict.staff]?.pdf_label || NA_LABEL}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ flex: 1, fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                              {relatedMap.classroom?.[session_dict.classroom]?.name || NA_LABEL}
                            </Typography>
                            <IconButton
                              className="session-edit-btn"
                              size="small"
                              onClick={() => handleElectiveEditClick(elective_dict.id, subject_group_dict.id, session_dict)}
                              sx={{
                                flexShrink: 0,
                                opacity: 0.1, // 0
                                transition: "opacity 0.15s",
                                p: 0.3,
                                color: "primary.main",
                                // ml: "auto",
                              }}
                            >
                              <EditTwoToneIcon sx={{ fontSize: "0.85rem" }} />
                            </IconButton>
                          </Box>
                        ))}
                        <Box
                          key={`${subject_group_dict.id}-link`}
                          sx={{
                            display: "flex",
                            width: "100%",
                            // alignItems: "center",
                            alignItems: "flex-end", // important: align to button bottom
                            gap: 1,
                            borderRadius: 1,
                            // bgcolor: i % 2 ? BG_COLOR_EVEN : BG_COLOR_ODD,
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <Button
                            variant='outlined'
                            startIcon={<AddBoxIcon sx={{ fontSize: "0.7rem", p: 0 }} />}
                            onClick={() => setElectiveOptionAdd({ elective_subject_template: elective_dict.id, subject_group_dict: subject_group_dict })}
                            sx={{
                              minHeight: "unset",
                              height: "auto",
                              padding: "0px 6px",
                              fontSize: "0.7rem",
                              lineHeight: 0.9,
                              textTransform: "none",
                              "&:hover": { color: "primary.main" },
                              "& .MuiButton-startIcon": {
                                marginRight: "4px",
                              },
                            }}
                          >
                            Add subject option
                          </Button>

                          {/* Dashed line */}
                          { grp_idx !== elective_group_template_list.length - 1 && (
                            <Box
                              sx={{
                                flex: 1,
                                borderTop: "1px dashed",
                                borderColor: "grey.300", // light grey
                                mb: "1px", // tweak to align perfectly with button bottom
                              }}
                            />
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  )
                })
              )}
            </Stack>
          </Box>
        </Card>
      );
    };

    ////////////////////////
    ////// Copy Classes ////
    ////////////////////////
    const handleCopyClassesClose = () => {
      setCopyClassesOpen(false);
    };

    const handleCopyClassesSave = async (addFormData, onAddSuccess, onAddFailure) => {
      const target_section = section_dict.id;
      const finalFormData = {
        ...addFormData,
        target_section: target_section,
      };
      
      await httpRequest({
        method: 'POST',
        url: COPY_CLASSES_API,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) => {
              if (row.id !== data.id) return row;

              return {
                ...row,
                ...data,
              };
            })
          );
          addNotification(`Imported ${getTotalSectionClasses(data)} classes!!!`);
        },
        onFailure: (data) => {
          onAddFailure(data);
        },
        addNotification,
      });
    };

    return (
      <Box>
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: 3
            }
          }
        }>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                <Typography variant="h5">{section_name}</Typography>
                {/* More data chips if needed */}
              </Box>
            }
            action={
              <Box>
                {/* Adding as hidden to keep header height consistent */}
                <IconButton size="small" sx={{ visibility: 'hidden' }}>
                  <EditTwoToneIcon fontSize="small" />
                </IconButton>

                { incomplete_data && (
                  <Tooltip title='Import classes from another section' arrow>
                    {/* <IconButton onClick={() => setSubjectAddOpen(true)} size="small" sx={{ p: 0}}>
                      <WarningRoundedIcon fontSize="small" color='warning' />
                    </IconButton> */}
                    <Button
                      variant='outlined'
                      startIcon={<LibraryAddIcon />}
                      onClick={() => setCopyClassesOpen(true)}
                      sx={{
                        minHeight: "unset",
                        height: "auto",
                        lineHeight: 0.8,
                        textTransform: "none",
                        "&:hover": { color: "primary.main" },
                        "& .MuiButton-startIcon": {
                          marginRight: "4px",
                        },
                      }}
                    >
                      Copy classes
                    </Button>
                  </Tooltip>
                )}
              </Box>
            }
            sx={{
              px: 2,
              py: 0,
              alignItems: 'center',
              '& .MuiCardHeader-action': {
                alignSelf: 'center',
              },
            }}
            slotProps={{
              content: { sx: { py: 1.5 } },
            }}
          />
          <Divider/>
          
          <CardContent sx={{ pt: 0, pb: 1, flexGrow: 1, display: 'flex' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 0.75, pt: 2, pb: 2 }}>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Total subject period:</Typography> {total_session}
              </Typography>
            </Box>

            {/* Expand Icon */}
            <CardActions disableSpacing sx={{ p: 0}}>
              <IconButton
                size="small"
                onClick={() => toggleExpand(section_dict.id)}
                sx={{ alignSelf: 'flex-start', mt: 1}}
              >
                <ExpandCircleDownOutlinedIcon
                  sx={{
                    transform: expandedMap[section_dict.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </IconButton>
            </CardActions>
          </CardContent>
        
          {/* Collapsible Content */}
          <Collapse in={expandedMap[section_dict.id]}>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <CardContent sx={{ bgcolor: EXPAND_BG_COLOR }}>
              <Stack spacing={1}>
                {/* Subjects Assignment Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" alignContent={'center'}>
                    <Typography component="span" variant="body2" fontWeight="bold" sx={{ mr: 1 }}>Subjects to be taught</Typography>
                  </Typography>
                  {/* Assign Subject Chip */}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddCircleIcon />}
                    onClick={() => setSubjectAddOpen(true)}
                    // sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
                  >
                    Assign subject
                  </Button>
                </Box>

                {/* Subject Chips */}
                <Box
                  sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(2, 1fr)',
                      lg: 'repeat(3, 1fr)',
                    },
                    gap: 1, // This creates even spacing without edge padding
                  }}
                >
                  {section_dict['subject_list'].map((subject_dict, index) => (
                    <SubjectCard
                      key={index}
                      section_row={index}
                      subject_dict={subject_dict}
                    />
                  ))}
                </Box>

                {/* <Divider sx={{ borderStyle: 'dashed' }} /> */}

                {/* Electives Assignment Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" alignContent={'center'}>
                    <Typography component="span" variant="body2" fontWeight="bold" sx={{ mr: 1 }}>Electives to be taught</Typography>
                  </Typography>
                  {/* Add Elective Chip */}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddCircleIcon />}
                    onClick={() => setNewElectiveAddOpen(true)}
                    // sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
                  >
                    Add Elective
                  </Button>
                </Box>

                {/* Elective Chips */}
                <Box
                  sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(2, 1fr)',
                      // lg: 'repeat(3, 1fr)',
                    },
                    gap: 1, // This creates even spacing without edge padding
                  }}
                >
                  {section_dict['elective_subject_template_list'].map((elective_dict, index) => (
                    <Box component="span" sx={{ mr: 1 }}>
                      <ElectiveCard
                        key={index}
                        section_row={index}
                        elective_dict={elective_dict}
                      />
                    </Box>
                  ))}
                </Box>
              </Stack>
            </CardContent>
          </Collapse>
        </Card>
        
        {/* Assign Subject Modal */}
        <Box>
          <AddModal
            open={isSubjectAddOpen}
            columns={subjectAddColumns}
            relatedData={relatedMap}
            rows={section_dict.subject_list}
            handleClose={handleSubjectAddClose}
            handleSave={handleSubjectAddSave}
            add_title={`${section_name} : Assign Subject`}
          />
        </Box>

        {/* Edit Subject Modal */}
        <Box>
          <EditModal
            open={isSubjectEditOpen}
            columns={subjectEditColumns}
            rowData={subjectEdit}
            relatedData={relatedMap}
            handleClose={handleSubjectEditClose}
            handleSave={handleSubjectEditSave}
            handleDelete={handleSubjectSessionDelete}
            edit_title={`Edit Session: ${section_name}`}
          />
        </Box>

        {/* Add New Elective Modal */}
        <Box>
          <AddModal
            open={isNewElectiveAddOpen}
            columns={newElectiveColumns}
            relatedData={relatedMap}
            rows={section_dict.elective_subject_template_list}
            handleClose={handleNewElectiveClose}
            handleSave={handleNewElectiveSave}
            add_title={`${section_name} : Add Elective`}
          />
        </Box>

        {/* Add Elective Modal */}
        <Box>
          <AddModal
            open={electiveAdd !== null}
            columns={electiveAddColumns}
            relatedData={relatedMap}
            rows={electiveAdd?.elective_group_template_list || []}
            handleClose={handleElectiveAddClose}
            handleSave={handleElectiveAddSave}
            add_title={`${section_name} : Add Elective Option`}
          />
        </Box>

        {/* Edit Elective Modal */}
        <Box>
          <EditModal
            open={isElectiveEditOpen}
            columns={electiveEditColumns}
            rowData={electiveEdit}
            relatedData={relatedMap}
            handleClose={handleElectiveEditClose}
            handleSave={handleElectiveEditSave}
            handleDelete={handleElectiveSessionDelete}
            edit_title={`${section_name}: Edit Elective Option`}
          />
        </Box>

        {/* Add Elective Option/Subject Modal */}
        <Box>
          <AddModal
            open={electiveOptionAdd !== null}
            columns={electiveOptionAddColumns}
            relatedData={relatedMap}
            rows={electiveOptionAdd?.subject_group_dict?.session_list || []}
            handleClose={handleElectiveOptionClose}
            handleSave={handleElectiveOptionSave}
            add_title={`${section_name} : Add Elective Option`}
          />
        </Box>

        {/* Copy Classes Modal */}
        <Box>
          <AddModal
            open={isCopyClassesOpen}
            columns={copyClassesColumns}
            relatedData={relatedMap}
            rows={[]}
            handleClose={handleCopyClassesClose}
            handleSave={handleCopyClassesSave}
            add_title={`${section_name} : Import Classes`}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Title>{TITLE}</Title>
          </Box>
          {/* Search Bar */}
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<InsightsIcon/>}
              onClick={handleLoadStatsClick}
              sx={{
                px: 4,
              }}
            >
              WorkLoad
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Scrollable table container */}
      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          // mt: 2,
          minHeight: 200, // Ensures proper space for loader
          display: 'flex',
          flexDirection: 'column', // Aligns table to the top when shown
          alignItems: isLoading ? 'center' : 'stretch', // Center loader, but stretch table
          justifyContent: isLoading ? 'center' : 'flex-start', // Center loader, start table at top
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            {rows.length === 0 ? (
              // Display message when there are no rows
              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  py: 2,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    xl: 'repeat(2, 1fr)',
                  },
                  gap: 1.5, // This creates even spacing without edge padding
                }}
              >
                {filteredRows.map((item_dict, index) => (
                  <Box key={index}>
                    <SectionCard
                      section_row={index}
                      section_dict={item_dict}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Fade>
        )}
      </Box>

      {/* Load Stats Modal */}
      <Box>
        <LoadStatsModal
          open={isLoadStatsModalOpen}
          handleClose={handleLoadStatsModalClose}
        />
      </Box>
    </Box>
  );
}
